import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { startImpersonation } from "@/lib/impersonate";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Eye, GraduationCap, Users, Search } from "lucide-react";
import { toast } from "sonner";

export default function PreviewAsPicker({ collapsed }) {
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState([]);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    if (!open) return;
    api.get("/admin/preview-targets").then(({ data }) => setTargets(data || [])).catch(() => toast.error("Could not load users"));
  }, [open]);

  const s = q.trim().toLowerCase();
  const filtered = targets.filter((t) =>
    !s || (t.name || "").toLowerCase().includes(s) || (t.email || "").toLowerCase().includes(s) ||
    (t.team_letter || "").toLowerCase() === s.replace("team ", ""));
  const mentors = filtered.filter((t) => t.role === "mentor");
  const students = filtered.filter((t) => t.role === "student");

  const go = async (t) => {
    setBusyId(t.id);
    try { await startImpersonation(t.id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start preview"); setBusyId(null); }
  };

  const Group = ({ label, icon: Icon, items }) => items.length === 0 ? null : (
    <div>
      <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] px-1 mb-1 flex items-center gap-1.5"><Icon size={10} /> {label} ({items.length})</div>
      <ul className="divide-y divide-[#E5E7EB] border border-[#E5E7EB]">
        {items.map((t) => (
          <li key={t.id}>
            <button onClick={() => go(t)} disabled={!!busyId}
              className="w-full text-left px-3 py-2 hover:bg-[#E6F5F5] flex items-center justify-between gap-3 disabled:opacity-50"
              data-testid={`preview-as-target-${t.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#0F1F2E] truncate">{t.name}</div>
                <div className="text-[11px] text-[#525252] truncate">{t.email}</div>
              </div>
              {t.team_letter && <span className="text-[10px] uppercase tracking-wider bg-[#0F1F2E] text-white px-1.5 py-0.5 font-bold shrink-0">Team {t.team_letter}</span>}
              {busyId === t.id && <span className="text-[10px] text-[#525252]">Loading…</span>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="preview-as-open-btn" title="Preview as…"
        className="w-full flex items-center justify-center gap-2 bg-[#25AAAA] text-white text-xs font-semibold uppercase tracking-[0.16em] px-3 py-2 hover:bg-[#1e7d7d] transition-colors">
        <Eye size={14} />
        {!collapsed && <span>Preview as…</span>}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[85vh] flex flex-col" data-testid="preview-as-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2"><Eye size={18} className="text-[#25AAAA]" /> Preview the platform as…</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#525252]">Pick any student or mentor. Your admin session is kept — use the yellow banner to return.</p>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
            <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or team letter…"
              className="rounded-none h-10 pl-9" data-testid="preview-as-search" />
          </div>
          <div className="overflow-y-auto flex-1 space-y-4 pr-1">
            <Group label="Mentors" icon={GraduationCap} items={mentors} />
            <Group label="Students" icon={Users} items={students} />
            {targets.length > 0 && filtered.length === 0 && <div className="text-sm text-[#525252] italic">No matches.</div>}
            {targets.length === 0 && <div className="text-sm text-[#525252] italic">No students or mentors yet.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
